import { ApplicationRef, inject, Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { interval } from 'rxjs';
import { concatMap, first } from 'rxjs/operators';
/**
 * Service de Mise à jour PWA
 *
 * Gère le cycle de vie des mises à jour de l'application en informant l'utilisateur via le Service Worker Angular.
 *
 * - Vérification : Scanne le serveur au démarrage, puis périodiquement (toutes les 3 min) une fois l'app stable.
 * - Téléchargement : Récupère les nouveaux fichiers en arrière-plan sans bloquer l'utilisateur.
 * - Interaction : Affiche une confirmation native (`confirm`) quand la mise à jour est prête à être installée.
 * - Validation : Rechargement de la page si l'utilisateur accepte, pour basculer sur la nouvelle version.
 * - Mise à jour forcée : Si pas de validation par l'utilisateur, lance un Timer de 1 minute avant le rechargement.
 */
@Injectable({
  providedIn: 'root',
})
export class SwUpdateService {
  private updates = inject(SwUpdate);
  private appRef = inject(ApplicationRef);

  constructor() {
    if (this.updates.isEnabled) {
      this.initUpdateMonitoring();
    }
  }

  private initUpdateMonitoring(): void {
    this.updates.versionUpdates.subscribe(evt => {
      switch (evt.type) {
        case 'VERSION_DETECTED':
          console.log(`Nouvelle version en téléchargement... : ${evt.version.hash}`);
          break;
        case 'VERSION_READY':
          console.log(`Version prête à être installée : ${evt.latestVersion.hash}`);
          this.promptUser();
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error(`Échec de l'installation de la version '${evt.version.hash}': ${evt.error}`);
          break;
      }
    });

    //attendre que l'app soit stable
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable));
    const checkInterval$ = interval(3 * 60 * 1000); // 3 minutes
    // const checkInterval$ = interval(5 * 1000); // 5 seconds pour test
    appIsStable$.pipe(concatMap(() => checkInterval$)).subscribe(async () => {
      try {
        await this.updates.checkForUpdate();
      } catch (err) {
        console.error('Erreur lors de la vérification périodique:', err);
      }
    });
  }

  private promptUser(): void {
    const message =
      `Pour continuer à utiliser l'application en toute sécurité et bénéficier des dernières corrections, une mise à jour est nécessaire. \n\n` +
      `Cliquez sur [OK] pour mettre à jour maintenant.\n` +
      `Cliquez sur [Annuler] pour reporter la mise à jour de 1 minute.`;

    const userAcceptedImmediateUpdate = confirm(message);

    if (userAcceptedImmediateUpdate) {
      this.performUpdate();
    } else {
      console.log('PWA: Mise à jour repoussée de 1 minute.');

      this.startForcedReloadTimer();
    }
  }

  private startForcedReloadTimer(): void {
    const gracePeriodTimer = 60 * 1000; // 1 minute
    //const gracePeriodTimer = 6 * 1000; // 6 secondes pour test
    setTimeout(() => {
      const message =
        `Pour continuer à utiliser l'application en toute sécurité et bénéficier des dernières corrections, une mise à jour est nécessaire. \n\n` +
        `En cliquant sur OK, votre page s'actualisera pour charger la nouvelle version.`;
      alert(message);
      this.performUpdate();
    }, gracePeriodTimer);
  }

  private performUpdate(): void {
    this.updates.activateUpdate().then(() => {
      document.location.reload();
    });
  }
}
